/*
 * <DevI18nToggle/> — in-app controls for the Quality Gate dev affordances
 * (UPT-172 / UPT-37 §3). Renders a small fixed widget, DEV builds only, that toggles
 * the pseudo-locale and the RTL preview. Renders null in production (and tree-shakes
 * out via the import.meta.env.DEV guard in devAffordancesEnabled()).
 *
 * Equivalent to the URL params `?lang=pseudo` and `?dir=rtl`; this just makes them
 * discoverable. Toggling reloads so the IntlProvider/direction pick up the change.
 */
import * as React from 'react';
import {
  devAffordancesEnabled,
  isPseudoLocaleActive,
  getDevDirOverride,
  setPseudoLocale,
  setDevDir,
} from './devLocale';

// Inline, logical-property styles so the widget itself passes Rule 3a.
const panel: React.CSSProperties = {
  position: 'fixed',
  insetBlockEnd: 72,
  insetInlineEnd: 8,
  zIndex: 9000,
  display: 'flex',
  gap: 6,
  padding: '6px 8px',
  borderRadius: 6,
  background: 'rgba(17,24,39,0.92)',
  color: '#fff',
  font: '12px/1.2 system-ui, sans-serif',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
};

const btn = (active: boolean): React.CSSProperties => ({
  cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: 4,
  padding: '2px 6px',
  background: active ? '#2563eb' : 'transparent',
  color: '#fff',
});

export default function DevI18nToggle(): React.ReactElement | null {
  if (!devAffordancesEnabled()) return null;

  const pseudo = isPseudoLocaleActive();
  const rtl = getDevDirOverride() === 'rtl';

  const togglePseudo = () => {
    setPseudoLocale(!pseudo);
    window.location.reload();
  };
  const toggleRtl = () => {
    setDevDir(rtl ? 'ltr' : 'rtl');
    window.location.reload();
  };

  return (
    <div style={panel} data-testid="dev-i18n-toggle" data-print-hide="true" aria-hidden>
      {/* eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: developer-only dev affordance, never shipped to end users */}
      <span style={{ opacity: 0.7 }}>i18n gate</span>
      <button type="button" style={btn(pseudo)} onClick={togglePseudo}>
        {/* eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: dev affordance label */}
        <span>pseudo</span>
      </button>
      <button type="button" style={btn(rtl)} onClick={toggleRtl}>
        RTL
      </button>
    </div>
  );
}
